import { Box } from "@repo/components/src/ui/box";
import { Card } from "@repo/components/src/ui/card";
import { Heading } from "@repo/components/src/ui/heading";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { CampaignAnalyticFragmentFragment } from "@repo/graphql/documents/campaign/fragment.generated";
import {
  Clock,
  CreditCard,
  Link2,
  Loader,
  LucideIcon,
  Package,
} from "lucide-react-native";
import React from "react";

type Progress = NonNullable<CampaignAnalyticFragmentFragment["progress"]>;

type Chip = { key: string; count: number; label: string; icon: LucideIcon };

const buildChips = (p: Progress): Chip[] => {
  const chips: Chip[] = [
    {
      key: "payment",
      count: p.pendingPaymentCount,
      icon: CreditCard,
      label: p.pendingPaymentCount === 1 ? "espera pago" : "esperan pago",
    },
    {
      key: "acceptance",
      count: p.awaitingAcceptanceCount,
      icon: Clock,
      label:
        p.awaitingAcceptanceCount === 1
          ? "espera respuesta del creador"
          : "esperan respuesta del creador",
    },
  ];
  if (p.hasRedemption) {
    chips.push({
      key: "redemption",
      count: p.awaitingRedemptionCount,
      icon: Package,
      label:
        p.awaitingRedemptionCount === 1
          ? "espera envío de canje"
          : "esperan envío de canje",
    });
  }
  chips.push(
    {
      key: "production",
      count: p.inProductionCount,
      icon: Loader,
      label:
        p.inProductionCount === 1
          ? "generando contenido"
          : "generando contenido",
    },
    {
      key: "link",
      count: p.awaitingLinkCount,
      icon: Link2,
      label:
        p.awaitingLinkCount === 1
          ? "esperando link del creador"
          : "esperando link del creador",
    },
  );
  return chips.filter((c) => c.count > 0);
};

type Props = {
  report: CampaignAnalyticFragmentFragment;
};

const ProgressStrip = React.memo(({ report }: Props) => {
  const p = report.progress;
  const total = p.totalPieces;
  const connected = p.connectedPieces;
  if (total === 0) return null;

  const pct = total ? Math.min(1, connected / total) : 0;
  const isComplete = connected >= total;
  const chips = buildChips(p);

  return (
    <Card variant="outline" className="rounded-2xl" size="md">
      <VStack space="xs">
        <HStack className="items-center" space="md">
          <Heading size="sm" className="text-typography-900">
            Avance de la campaña
          </Heading>
          <Text size="xs" className="text-typography-500">
            {isComplete
              ? `Todas conectadas · ${total} de ${total} 🎉`
              : `${connected} de ${total} publicaciones conectadas`}
          </Text>
          <Box className="flex-1" />
          <Text size="sm" className="font-bold text-typography-900">
            {Math.round(pct * 100)} %
          </Text>
        </HStack>

        <Box className="h-1.5 bg-background-100 rounded-full overflow-hidden">
          <Box
            className="bg-brand-500 h-full"
            style={{ width: `${pct * 100}%` } as object}
          />
        </Box>

        {chips.length > 0 ? (
          <HStack className="flex-wrap gap-x-1.5 gap-y-1 pt-1">
            {chips.map((c) => (
              <HStack
                key={c.key}
                space="xs"
                className="items-center bg-brand-50 px-2 py-0.5 rounded-full"
              >
                <Icon as={c.icon} size="2xs" className="text-brand-500" />
                <Text size="xs" className="text-brand-700 font-semibold">
                  {c.count}
                </Text>
                <Text size="xs" className="text-brand-700">
                  {c.label}
                </Text>
              </HStack>
            ))}
          </HStack>
        ) : null}
      </VStack>
    </Card>
  );
});

ProgressStrip.displayName = "ProgressStrip";

export default ProgressStrip;
